/**
 * Size guide tabs (snippets/pdp-size-guides-modal.liquid).
 *
 * The size charts matched with the product are rendered by Liquid, so the modal opens instantly. Every other static
 * chart is fetched once, the first time the modal is opened, with the Section Rendering API
 * (sections/hp-size-charts.liquid) and appended as extra tabs. When Liquid rendered nothing (no match), the fetched
 * section is the whole content of the modal.
 */
class PdpSizeGuideTabs extends HTMLElement {
  /**
   * The section does not depend on the product, so a single request is shared by every instance of the page.
   */
  static fetchSection(url) {
    const requests = (PdpSizeGuideTabs._requests = PdpSizeGuideTabs._requests || new Map());

    if (!requests.has(url)) {
      requests.set(
        url,
        fetch(url)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Size charts request failed: ${response.status}`);
            }

            return response.text();
          })
          .catch((error) => {
            requests.delete(url);
            throw error;
          })
      );
    }

    return requests.get(url);
  }

  connectedCallback() {
    // <x-modal> moves itself to the body while it is open, so this callback runs on every open and close.
    if (this._initialized) {
      return;
    }

    this._initialized = true;
    this._tabList = this.querySelector("[data-size-guide-tablist]");
    this._panelList = this.querySelector("[data-size-guide-panels]");
    this._status = this.querySelector("[data-size-guide-status]");
    this._modal = this.closest("x-modal");
    // The chart of the product, as selected by Liquid: the modal comes back to it every time it is opened
    this._defaultKey = this.selectedTab?.dataset.sizeGuideKey;

    this._tabList.addEventListener("click", (event) => {
      const tab = event.target.closest("[data-size-guide-tab]");

      if (tab) {
        this.activate(tab);
      }
    });
    this._tabList.addEventListener("keydown", this._onKeyDown.bind(this));
    this._tabList.addEventListener("wheel", this._onWheel.bind(this), { passive: false });

    this._modal?.addEventListener("dialog:before-show", () => {
      this._selectDefault();
      this.warm();
      this.load();
    });
    this._modal?.addEventListener("dialog:after-show", () => {
      this._updateStickyOffset();
      this._revealTab(this.selectedTab, "auto");
    });

    this._setup();
  }

  get tabs() {
    return Array.from(this._tabList.querySelectorAll("[data-size-guide-tab]"));
  }

  get selectedTab() {
    return this._tabList.querySelector('[data-size-guide-tab][aria-selected="true"]');
  }

  /**
   * Select a tab and show its panel
   */
  activate(tab, { focus = false } = {}) {
    const changed = tab !== this.selectedTab;

    this.tabs.forEach((item) => {
      const isSelected = item === tab;
      const panel = this._panelFor(item);

      item.setAttribute("aria-selected", isSelected ? "true" : "false");
      item.tabIndex = isSelected ? 0 : -1;

      if (panel) {
        panel.hidden = !isSelected;
      }
    });

    if (focus) {
      tab.focus({ preventScroll: true });
    }

    if (changed) {
      // Charts have very different heights: start the newly shown one from its top
      this._modal?.getShadowPartByName?.("content")?.scrollTo({ top: 0 });
    }

    if (this._modal?.hasAttribute("open")) {
      this.warm();
    }

    this._revealTab(tab);
  }

  /**
   * Load the image of the selected chart right away instead of waiting for the lazy loading. Besides the tab changes,
   * it is called when the customer hovers, focuses or touches the trigger, so that the chart is already there when the
   * modal opens.
   */
  warm() {
    const selectedTab = this.selectedTab;
    const image = selectedTab ? this._panelFor(selectedTab)?.querySelector("img") : null;

    if (image && image.loading === "lazy") {
      image.loading = "eager";
    }
  }

  /**
   * Fetch all the static size charts and append the ones that Liquid did not render
   */
  load() {
    const url = this.getAttribute("section-url");

    if (!url || this._loaded) {
      return Promise.resolve();
    }

    if (!this._request) {
      this._setStatus("loading");

      this._request = PdpSizeGuideTabs.fetchSection(url)
        .then((html) => {
          this._append(html);
          this._loaded = true;
          this._setStatus(null);
        })
        .catch(() => {
          // The request is retried the next time the modal is opened
          this._request = null;
          this._setStatus("error");
        });
    }

    return this._request;
  }

  _selectDefault() {
    const tab = this.tabs.find((item) => item.dataset.sizeGuideKey === this._defaultKey);

    if (tab && tab !== this.selectedTab) {
      this.activate(tab);
    }
  }

  _panelFor(tab) {
    const key = tab.dataset.sizeGuideKey;

    return Array.from(this._panelList.children).find((panel) => panel.dataset.sizeGuideKey === key);
  }

  _append(html) {
    const fragment = new DOMParser().parseFromString(html, "text/html");
    const knownKeys = new Set(this.tabs.map((tab) => tab.dataset.sizeGuideKey));
    const panels = Array.from(fragment.querySelectorAll("[data-size-guide-panel]"));

    fragment.querySelectorAll("[data-size-guide-tab]").forEach((tab) => {
      const key = tab.dataset.sizeGuideKey;
      const panel = panels.find((item) => item.dataset.sizeGuideKey === key);

      if (!key || !panel || knownKeys.has(key)) {
        return;
      }

      knownKeys.add(key);
      tab.setAttribute("aria-selected", "false");
      panel.hidden = true;

      this._tabList.append(tab);
      this._panelList.append(panel);
    });

    this._setup();
  }

  /**
   * Link tabs and panels, and make sure that exactly one tab is selected. It is called again once new tabs are added.
   */
  _setup() {
    const tabs = this.tabs;
    const prefix = this._modal?.id || "pdp-size-guide";

    tabs.forEach((tab) => {
      const panel = this._panelFor(tab);
      const key = tab.dataset.sizeGuideKey;

      tab.id = `${prefix}-tab-${key}`;

      if (panel) {
        panel.id = `${prefix}-panel-${key}`;
        panel.setAttribute("aria-labelledby", tab.id);
        tab.setAttribute("aria-controls", panel.id);
      }
    });

    // A single tab is only shown as a tab when more of them are about to be loaded
    this._tabList.hidden = tabs.length === 0 || (tabs.length === 1 && !this.hasAttribute("section-url"));

    if (tabs.length > 0) {
      this.activate(this.selectedTab || tabs[0]);
    }
  }

  /**
   * The loading and error messages are only needed when there is nothing else to show
   */
  _setStatus(state) {
    if (!this._status) {
      return;
    }

    this._status.hidden = state === null || this.tabs.length > 0;
    this._status.querySelector("[data-size-guide-loading]").hidden = state !== "loading";
    this._status.querySelector("[data-size-guide-error]").hidden = state !== "error";
  }

  /**
   * Scroll the tab list (and only the tab list) so that the given tab is centered
   */
  _revealTab(tab, behavior = "smooth") {
    if (!tab || this._tabList.scrollWidth <= this._tabList.clientWidth) {
      return;
    }

    this._tabList.scrollTo({
      left: tab.offsetLeft - (this._tabList.clientWidth - tab.offsetWidth) / 2,
      behavior,
    });
  }

  /**
   * The tab list sticks below the header of the modal, whose height depends on the breakpoint
   */
  _updateStickyOffset() {
    const header = this._modal?.getShadowPartByName?.("header");

    if (header) {
      this.style.setProperty("--pdp-size-guides-sticky-offset", `${header.offsetHeight}px`);
    }
  }

  /**
   * As per https://www.w3.org/WAI/ARIA/apg/patterns/tabs/, arrows move between the tabs
   */
  _onKeyDown(event) {
    const tabs = this.tabs;
    const index = tabs.indexOf(document.activeElement);

    if (index === -1) {
      return;
    }

    let nextIndex;

    switch (event.key) {
      case "ArrowLeft":
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case "ArrowRight":
        nextIndex = (index + 1) % tabs.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.activate(tabs[nextIndex], { focus: true });
  }

  /**
   * With a mouse, the wheel scrolls the (long) tab list sideways
   */
  _onWheel(event) {
    const list = this._tabList;

    if (list.scrollWidth <= list.clientWidth || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
      return;
    }

    event.preventDefault();
    list.scrollLeft += event.deltaY;
  }
}

if (!window.customElements.get("pdp-size-guide-tabs")) {
  window.customElements.define("pdp-size-guide-tabs", PdpSizeGuideTabs);

  // The trigger is re-rendered with the variant picker, hence the delegation
  const warmSizeGuide = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest("[data-size-guide-trigger]") : null;

    if (trigger) {
      document.getElementById(trigger.getAttribute("aria-controls"))?.querySelector("pdp-size-guide-tabs")?.warm();
    }
  };

  ["pointerover", "focusin", "touchstart"].forEach((type) => {
    document.addEventListener(type, warmSizeGuide, { passive: true });
  });
}
